import React from "react";
import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Edit3, Check, Users, Shield, Building2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useUpdateOrganizationMember } from "@/hooks/api-hooks";

const editMemberSchema = z.object({
  firstName: z.string().min(1, "First name must be at least 1 character").optional(),
  lastName: z.string().min(1, "Last name must be at least 1 character").optional(),
  role: z.enum(['owner', 'admin', 'manager', 'staff'], {
    required_error: "Please select a role",
  }),
  departmentId: z.string().optional(),
  managerId: z.string().optional(),
  teamIds: z.array(z.string()).default([]),
  isActive: z.boolean().default(true),
});

type EditMemberFormData = z.infer<typeof editMemberSchema>;

const normalizeRole = (role?: string | null): EditMemberFormData['role'] => {
  const value = (role ?? '').toString().toLowerCase();
  switch (value) {
    case 'owner':
      return 'owner';
    case 'admin':
      return 'admin';
    case 'manager':
    case 'team_lead':
    case 'team lead':
    case 'lead':
      return 'manager';
    default:
      return 'staff';
  }
};

interface Member {
  id: string;
  firstName?: string;
  lastName?: string;
  organizationRole?: string;
  email?: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  role: 'owner' | 'admin' | 'manager' | 'staff' | 'member' | 'team_lead' | 'MANAGER' | 'TEAM_LEAD' | 'MEMBER';
  departmentId?: string;
  department?: string;
  managerId?: string;
  teamIds?: string[];
  isActive: boolean;
}

interface Manager {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  role: string;
  firstName?: string;
  lastName?: string;
  organizationRole?: string;
}

interface Team {
  id: string;
  name: string;
  description?: string;
}

interface EditMemberModalProps {
  member: Member | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (memberData: any) => void;
  organizationId: string;
  managers: Manager[];
  teams?: Team[];
  context?: 'organization' | 'team';
  onSubmitOverride?: (data: EditMemberFormData) => Promise<void>;
}

export function EditMemberModal({
  member,
  isOpen,
  onClose,
  onSuccess,
  organizationId,
  managers,
  teams = [],
  context,
  onSubmitOverride,
}: EditMemberModalProps) {
  const updateMemberMutation = useUpdateOrganizationMember();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const effectiveContext = context ?? 'organization';

  const form = useForm<EditMemberFormData>({
    resolver: zodResolver(editMemberSchema),
    defaultValues: {
      firstName: member?.user?.firstName || member?.firstName || "",
      lastName: member?.user?.lastName || member?.lastName || "",
      role: normalizeRole(member?.role || member?.organizationRole),
      departmentId: member?.departmentId || member?.department || "",
      managerId: member?.managerId || "",
      teamIds: member?.teamIds || [],
      isActive: member?.isActive ?? true,
    },
  });

  const watchedRole = form.watch("role");
  const watchedTeamIds = form.watch("teamIds");

  // Update form when member changes
  React.useEffect(() => {
    if (member) {
      form.reset({
        firstName: member.user?.firstName || member.firstName,
        lastName: member.user?.lastName || member.lastName,
        role: normalizeRole(member.role || member.organizationRole),
        departmentId: member.departmentId || member.department || "",
        managerId: member.managerId || "",
        teamIds: member.teamIds || [],
        isActive: member.isActive,
      });
    }
  }, [member, form]);

  const handleTeamToggle = (teamId: string, checked: boolean) => {
    const currentTeamIds = form.getValues("teamIds");
    if (checked) {
      form.setValue("teamIds", [...currentTeamIds, teamId]);
    } else {
      form.setValue("teamIds", currentTeamIds.filter(id => id !== teamId));
    }
  };

  const onSubmit = async (data: EditMemberFormData) => {
    if (!member) return;

    if (onSubmitOverride) {
      setIsSubmitting(true);
      try {
        await onSubmitOverride(data);
        onSuccess?.(data);
        onClose();
      } catch (error: any) {
        console.error('Error updating member:', error);
        toast.error(error?.message || 'Failed to update member. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    updateMemberMutation.mutate(
      {
        organizationId,
        memberId: member.id,
        role: data.role,
        firstName: data.firstName,
        lastName: data.lastName,
        departmentId: data.departmentId,
        isActive: data.isActive,
      },
      {
        onSuccess: (response) => {
          const firstName = member.user?.firstName || member.firstName;
          const lastName = member.user?.lastName || member.lastName;
          toast.success(`${firstName} ${lastName} updated successfully!`);

          onSuccess?.(response);
          onClose();
        },
        onError: (error: any) => {
          console.error("Error updating member:", error);
          toast.error(`Failed to update member: ${error?.message || 'Please try again.'}`);
        }
      }
    );
  };

  const handleClose = () => {
    onClose();
  };

  const roleOptions = effectiveContext === 'team'
    ? [
        { value: 'manager', label: 'Manager', description: 'Team manager with full access' },
        { value: 'team_lead', label: 'Team Lead', description: 'Coordinates day-to-day work' },
        { value: 'member', label: 'Member', description: 'Standard team member access' },
      ]
    : [
        { value: 'owner', label: 'Owner', description: 'Full ownership access' },
        { value: 'admin', label: 'Admin', description: 'Full organization access' },
        { value: 'manager', label: 'Manager', description: 'Team management permissions' },
        { value: 'staff', label: 'Staff', description: 'Regular organization member' },
      ];

  const getRoleColor = (role: string) => {
    const normalized = role.toLowerCase();
    switch (normalized) {
      case 'owner':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'manager':
        return 'bg-blue-100 text-blue-800';
      case 'team_lead':
        return 'bg-indigo-100 text-indigo-800';
      case 'staff':
      case 'member':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!member) return null;

  // Filter out the current member from the managers list
  const availableManagers = (managers || []).filter(manager =>
    (manager.user?.id || manager.id) !== (member.user?.id || member.id)
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center">
              <Edit3 className="h-4 w-4 text-blue-600" />
            </div>
            Edit Member
          </DialogTitle>
          <div className="text-sm text-muted-foreground">
            {member.user?.email || member.email}
          </div>
        </DialogHeader>

        <Form {...(form as any)}>
          <form onSubmit={form.handleSubmit(onSubmit as any)} className="space-y-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control as any}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control as any}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control as any}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Role *
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {roleOptions.map((roleOption) => (
                        <SelectItem key={roleOption.value} value={roleOption.value}>
                          <div className="flex items-center gap-2">
                            <Badge className={getRoleColor(roleOption.value)}>{roleOption.label}</Badge>
                            <span className="text-xs text-muted-foreground">{roleOption.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {effectiveContext === 'organization' && (watchedRole === 'manager' || watchedRole === 'staff') && availableManagers.length > 0 && (
              <FormField
                control={form.control as any}
                name="managerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Direct Manager
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a manager (optional)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="">
                          <span className="text-muted-foreground">No manager assigned</span>
                        </SelectItem>
                        {availableManagers.map((manager) => (
                          <SelectItem key={manager.user?.id || manager.id} value={manager.user?.id || manager.id}>
                            <div className="flex items-center gap-2">
                              <span>
                                {manager.user?.firstName || manager.firstName} {manager.user?.lastName || manager.lastName}
                              </span>
                              <Badge variant="secondary" className="text-xs">
                                {manager.role || manager.organizationRole}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {effectiveContext === 'organization' && (
              <FormField
                control={form.control as any}
                name="departmentId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Department
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Engineering" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {effectiveContext === 'organization' && (
            <FormField
              control={form.control as any}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Active Member</FormLabel>
                      <div className="text-sm text-muted-foreground">
                        Enable to allow this member to access the organization
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            {effectiveContext === 'organization' && teams.length > 0 && (
              <div className="space-y-4">
                <FormLabel className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Team Assignments
                </FormLabel>
                <div className="grid grid-cols-1 gap-3 max-h-48 overflow-y-auto">
                  {teams.map((team) => (
                    <div key={team.id} className="flex items-center space-x-3 p-3 border border-border rounded-lg">
                      <Checkbox
                        checked={watchedTeamIds.includes(team.id)}
                        onCheckedChange={(checked) => handleTeamToggle(team.id, checked as boolean)}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">{team.name}</div>
                        {team.description && (
                          <div className="text-xs text-muted-foreground">{team.description}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {watchedTeamIds.length > 0 && (
                  <div className="text-sm text-muted-foreground">
                    {watchedTeamIds.length} team{watchedTeamIds.length !== 1 ? 's' : ''} selected
                  </div>
                )}
              </div>
            )}
          </div>

            <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting || updateMemberMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || updateMemberMutation.isPending}
                className="gap-2"
              >
                {isSubmitting || updateMemberMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-border border-t-foreground"></div>
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Save Changes
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
